import { differenceInCalendarDays } from 'date-fns'
import type { AppointmentRecord } from '../types'
import { parseAppointmentDate } from './parseAppointmentDate'

/**
 * Resolve the calendar day an appointment belongs to. Prefers
 * `source_appointment_date` — the clinic's own zone-free calendar day, and
 * the column the range query is bounded on — so the day bucket never depends
 * on the viewer's browser timezone. `start_time` is only a fallback for rows
 * missing the date column (and is what the card displays / sorts by).
 */
export function getAppointmentDate(
  appointment: AppointmentRecord
): Date | null {
  const candidates = [
    appointment.source_appointment_date,
    appointment.start_time,
  ]
  for (const value of candidates) {
    const date = parseAppointmentDate(value)
    if (date) return date
  }
  return null
}

/**
 * Sort key: timed appointments in start order, date-only ones last. The
 * server sorts on a bare `date` column, so intra-day order is unspecified
 * server-side and decided here. The sentinel is MAX_SAFE_INTEGER rather than
 * Infinity on purpose: two date-only rows compare as `Infinity - Infinity`
 * = NaN, which breaks the comparator.
 */
export function getAppointmentSortKey(appointment: AppointmentRecord): number {
  const start = parseAppointmentDate(appointment.start_time)
  return start ? start.getTime() : Number.MAX_SAFE_INTEGER
}

/** Match the calendar's day buckets and within-day card order. */
export function visibleCalendarAppointments(
  appointments: AppointmentRecord[],
  rangeStart: Date,
  rangeDays: number
): AppointmentRecord[] {
  return appointments
    .flatMap((appointment) => {
      const date = getAppointmentDate(appointment)
      const day = date ? differenceInCalendarDays(date, rangeStart) : -1
      return day >= 0 && day < rangeDays ? [{ appointment, day }] : []
    })
    .toSorted(
      (a, b) =>
        a.day - b.day ||
        getAppointmentSortKey(a.appointment) -
          getAppointmentSortKey(b.appointment)
    )
    .map((entry) => entry.appointment)
}
